import { useRef, useEffect, useContext } from 'react';
import {
  To,
  useNavigate,
  useLocation,
  UNSAFE_NavigationContext as NavigationContext
} from 'react-router';

type WindowWithNavigation = Window & {
  allowNavigation?: () => void;
};

interface UseNavigationControlProps {
  activeStep: number;
  stepperLength: number;
  backToPath?: To;
  customCancelHandler: ((path?: To) => void) | null;
  hasChangedFields: boolean;
  onShowLeavingDialog: (blockedPath: To) => void;
}

export const useNavigationControl = ({
  activeStep,
  stepperLength,
  backToPath,
  customCancelHandler,
  hasChangedFields,
  onShowLeavingDialog
}: UseNavigationControlProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigator } = useContext(NavigationContext);

  const allowNavigationRef = useRef(false);
  const blockedNavigationRef = useRef<{ to: To; state?: unknown } | null>(null);
  const customCancelHandlerRef = useRef<((path?: To) => void) | null>(null);
  const backToPathRef = useRef<To | undefined>();
  const hasChangedFieldsRef = useRef<boolean>(false);

  useEffect(() => {
    customCancelHandlerRef.current = customCancelHandler;
  }, [customCancelHandler]);

  useEffect(() => {
    backToPathRef.current = backToPath;
  }, [backToPath]);

  useEffect(() => {
    hasChangedFieldsRef.current = hasChangedFields;
  }, [hasChangedFields]);

  const allowNavigation = () => {
    allowNavigationRef.current = true;
    (window as WindowWithNavigation).allowNavigation?.();
  };

  const handleCancelClick = () => {
    allowNavigation();
    let blockedPath = undefined;
    if (blockedNavigationRef.current) {
      const { to } = blockedNavigationRef.current;
      blockedPath = to;
    }
    const path = blockedPath ?? backToPathRef.current;
    if (customCancelHandlerRef.current) {
      customCancelHandlerRef.current(path);
    } else {
      path ? navigate(path) : navigate(-1);
    }
  };

  const checkUnsavedChanges = () => {
    const isLastStep = activeStep === stepperLength + 1;
    return hasChangedFieldsRef.current && !isLastStep;
  };

  useEffect(() => {
    (window as WindowWithNavigation).allowNavigation = () => {
      allowNavigationRef.current = true;
    };
    return () => {
      delete (window as WindowWithNavigation).allowNavigation;
    };
  }, []);

  useEffect(() => {
    const originalNavigationFunction = navigator.push.bind(navigator);
    const originalReplaceFunction = navigator.replace.bind(navigator);

    const handleNavigation = (
      originalFn: typeof originalNavigationFunction,
      to: To,
      state?: unknown
    ) => {
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        originalFn(to, state);
      } else {
        const isLastStep = activeStep === stepperLength + 1;
        if (isLastStep) {
          allowNavigationRef.current = true;
          originalFn(to, state);
        } else {
          blockedNavigationRef.current = { to, state };
          if (location.pathname.includes('benefit-set') || location.pathname.includes('bonus')) {
            onShowLeavingDialog(to);
          } else {
            handleCancelClick();
          }
        }
      }
    };

    navigator.push = (to: To, state?: unknown) => {
      handleNavigation(originalNavigationFunction, to, state);
    };

    navigator.replace = (to: To, state?: unknown) => {
      handleNavigation(originalReplaceFunction, to, state);
    };

    return () => {
      navigator.push = originalNavigationFunction;
      navigator.replace = originalReplaceFunction;
    };
  }, [navigator, activeStep]);

  return {
    allowNavigation,
    handleCancelClick,
    checkUnsavedChanges
  };
};
